<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { ChapterConfig } from '../configs/types'
import { getChapterConfig } from '../configs/brands'
import { usePurchase } from '../composables/usePurchase'
import { useSubscription } from '../composables/useSubscription'
import { createShowPaymentMethodSelector } from '../composables/usePaymentMethodDisplay'
import { useBottomSwiper } from '../composables/useBottomSwiper'
import SubscribeItemContent from './SubscribeItemContent.novelarc.vue'
import PurchaseItemContent from './PurchaseItemContent.novelarc.vue'
import PaymentMethodSelector from './PaymentMethodSelector.vue'
import { useUserStore } from '@/stores'
import 'swiper/css'

const props = defineProps<{
  value: boolean
  contentId: string
  contentChapterId: string
  bookName: string
  chapterName: string
  serialStatus: number
}>()

const emit = defineEmits<{
  (e: 'input', value: boolean): void
  (e: 'success'): void
}>()

// 加载当前品牌的 chapter 配置
const config: ChapterConfig = getChapterConfig()

const userStore = useUserStore()

// 使用充值 composable（支持多支付方式）
const {
  templateProducts,
  PayTemplateSuccess,
  PayTemplate,
  currentPaymentMethod: purchasePaymentMethod,
  availablePaymentMethods: purchaseAvailableMethods,
  selectPaymentMethod: selectPurchasePaymentMethod,
  productLists: purchaseProductLists,
} = usePurchase('chapter', {
  contentId: props.contentId,
  contentChapterId: props.contentChapterId,
  bookName: props.bookName,
  chapterName: props.chapterName,
  serialStatus: props.serialStatus,
})

// 使用订阅 composable（支持多支付方式）
const {
  loaded,
  subscribeSuccess,
  getUnitPrice,
  onSubscribeProducts,
  isZero,
  availablePaymentMethods: subscriptAvailableMethods,
  selectPaymentMethod: selectSubscriptPaymentMethod,
  currentPaymentMethod: subscriptPaymentMethod,
  hasPayPalPasswordlessPayment,
} = useSubscription('chapter', {
  contentId: props.contentId,
  contentChapterId: props.contentChapterId,
})

const active = ref(0)

// 统一的支付方式状态
const currentPaymentMethod = computed({
  get: () => {
    if (!purchaseProductLists.value || purchaseProductLists.value.length === 0)
      return subscriptPaymentMethod.value
    return purchasePaymentMethod.value
  },
  set: (val) => {
    selectPurchasePaymentMethod(val)
    selectSubscriptPaymentMethod(val)
  },
})

// 统一的可用支付方式
const availablePaymentMethods = computed(() => {
  if (hasPayPalPasswordlessPayment.value) {
    const all = [...(purchaseAvailableMethods.value || []), ...(subscriptAvailableMethods.value || [])]
    const mergedUnique = all.filter((m, i, arr) =>
      arr.findIndex(x => String(x.paymentMethod).toLowerCase() === String(m.paymentMethod).toLowerCase()) === i,
    )
    return mergedUnique.filter(m => String(m.paymentMethod).toLowerCase() === 'paypal')
  }

  const purchaseMethods = purchaseAvailableMethods.value || []
  const subscriptMethods = subscriptAvailableMethods.value || []

  if (purchaseMethods.length > subscriptMethods.length)
    return purchaseMethods
  if (subscriptMethods.length > purchaseMethods.length)
    return subscriptMethods

  if (purchaseMethods.length === 1 && subscriptMethods.length === 1) {
    const p = String(purchaseMethods[0]?.paymentMethod ?? '')
    const s = String(subscriptMethods[0]?.paymentMethod ?? '')
    if (p === s)
      return purchaseMethods
    const merged = [...purchaseMethods, ...subscriptMethods].filter(
      (m, i, arr) => arr.findIndex(x => String(x.paymentMethod) === String(m.paymentMethod)) === i,
    )
    return merged
  }

  return purchaseMethods
})

const showPaymentMethodSelector = createShowPaymentMethodSelector(availablePaymentMethods)

const subscribes = computed(() => {
  const currentMethod = Number(currentPaymentMethod.value)
  if (!subscriptAvailableMethods.value || !Array.isArray(subscriptAvailableMethods.value))
    return []
  const currentMethodList = subscriptAvailableMethods.value.find(
    (list: any) => Number(list?.paymentMethod) === currentMethod,
  )
  if (!currentMethodList?.products || !Array.isArray(currentMethodList.products))
    return []
  return currentMethodList.products.filter((item: any) => item && item.purchasePopShow)
})

const isPurchaseFirst = computed(() => {
  const purchaseCount = templateProducts.value?.length ?? 0
  const subscribeCount = subscribes.value?.length ?? 0
  return purchaseCount > subscribeCount
})

// isPurchaseFirst 模式下，底部轮播展示订阅套餐（独立实例）
const subscribeSwiperRef = ref<HTMLElement>()
const {
  slideIndex: subscribeSlideIndex,
  init: initSubscribeSwiper,
  destroy: destroySubscribeSwiper,
  onBulletClick: onSubscribeBulletClick,
} = useBottomSwiper(subscribeSwiperRef, () => subscribes.value)

// !isPurchaseFirst 模式下，底部轮播展示充值套餐（独立实例）
const purchaseSwiperRef = ref<HTMLElement>()
const {
  slideIndex: purchaseSlideIndex,
  init: initPurchaseSwiper,
  destroy: destroyPurchaseSwiper,
  onBulletClick: onPurchaseBulletClick,
} = useBottomSwiper(purchaseSwiperRef, () => templateProducts.value ?? [])

function handlePayTemplate(item: any, index: number) {
  active.value = index
  PayTemplate(item)
}

function handleSubscribeProducts(item: any) {
  // 确保订阅使用统一的支付方式（避免两个 hook 各自初始化后状态不一致）
  selectSubscriptPaymentMethod(currentPaymentMethod.value)
  onSubscribeProducts(item)
}

function shouldInitSubscribeSwiper() {
  return loaded.value && props.value && isPurchaseFirst.value && !userStore.$state.subscribe && (subscribes.value?.length ?? 0) > 0
}

function shouldInitPurchaseSwiper() {
  return loaded.value && props.value && !isPurchaseFirst.value && (templateProducts.value?.length ?? 0) > 0
}

watch(
  () => props.value,
  (newVal) => {
    if (!newVal)
      return
    if (shouldInitSubscribeSwiper())
      initSubscribeSwiper()
    if (shouldInitPurchaseSwiper())
      initPurchaseSwiper()
  },
)

watch(
  () => loaded.value,
  (val) => {
    if (!val)
      return
    if (shouldInitSubscribeSwiper())
      initSubscribeSwiper()
    if (shouldInitPurchaseSwiper())
      initPurchaseSwiper()
  },
)

watch(
  () => templateProducts.value,
  (newVal) => {
    if (loaded.value && newVal?.length && props.value)
      initPurchaseSwiper()
  },
)

watch(
  () => subscribes.value,
  (newVal) => {
    if (isPurchaseFirst.value && loaded.value && newVal?.length && props.value)
      initSubscribeSwiper()
  },
)

watch(
  currentPaymentMethod,
  () => {
    if (shouldInitSubscribeSwiper())
      initSubscribeSwiper()
    else
      destroySubscribeSwiper()

    if (shouldInitPurchaseSwiper())
      initPurchaseSwiper()
    else
      destroyPurchaseSwiper()
  },
)

function handleVisibilityChange() {
  if (document.hidden)
    return
  if (shouldInitSubscribeSwiper())
    initSubscribeSwiper()
  if (shouldInitPurchaseSwiper())
    initPurchaseSwiper()
}

onMounted(() => {
  document.addEventListener('visibilitychange', handleVisibilityChange)
})

onBeforeUnmount(() => {
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  destroySubscribeSwiper()
  destroyPurchaseSwiper()
})

watch(
  () => subscribeSuccess.value,
  (val) => {
    if (val)
      emit('success')
  },
)

watch(
  () => PayTemplateSuccess.value,
  (val) => {
    if (val)
      emit('success')
  },
)
</script>

<template>
  <BasicPopup :value="value" @input="value => $emit('input', value)">
    <div class="popup-recharge-novelarc w-full px-5 pt-20px pb-30px bg-n-bg-card rounded-t-lg relative">
      <h3 class="text-18px text-center text-n-text-1">
        {{ config.texts.payTemplateTitle }}
      </h3>
      <p class="text-14px text-n-text-2 text-center">
        {{ config.texts.payTemplateDesc }}
      </p>
      <template v-if="showPaymentMethodSelector">
        <PaymentMethodSelector
          :model-value="currentPaymentMethod"
          :available-methods="availablePaymentMethods"
          @update:model-value="val => currentPaymentMethod = val"
        />
      </template>

      <!-- 充值在上（平铺） + 订阅在下（轮播） -->
      <div v-if="isPurchaseFirst" class="payment-content-wrapper" :class="{ 'mt-16px': !showPaymentMethodSelector }">
        <div v-if="templateProducts?.length" class="purchase-list-container">
          <div class="purchase-list">
            <div
              v-for="(item, index) in templateProducts"
              :key="index"
              class="purchase-list-item rounded-8px mb-3"
              @click="handlePayTemplate(item, index)"
            >
              <PurchaseItemContent :item="item" :is-active="active === index" />
            </div>
          </div>
        </div>

        <div v-if="!userStore.$state.subscribe && subscribes?.length" ref="subscribeSwiperRef" class="bottom-swiper subscribe-swiper-mode swiper mt-1">
          <div class="swiper-wrapper">
            <div
              v-for="(item, index) in subscribes"
              :key="index"
              class="swiper-slide bottom-swiper-item"
              @click="handleSubscribeProducts(item)"
            >
              <SubscribeItemContent :item="item" :get-unit-price="getUnitPrice" :is-zero="isZero" />
            </div>
          </div>
          <div v-if="subscribes.length > 1" class="bottom-swiper-pagination">
            <span
              v-for="(_, i) in subscribes"
              :key="i"
              class="swiper-pagination-bullet"
              :class="{ 'swiper-pagination-bullet-active': i === subscribeSlideIndex }"
              @click="onSubscribeBulletClick(i)"
            />
          </div>
        </div>
      </div>

      <!-- 订阅在上（平铺） + 充值在下（轮播） -->
      <div v-else class="payment-content-wrapper" :class="{ 'mt-16px': !showPaymentMethodSelector }">
        <div v-if="!userStore.$state.subscribe && subscribes?.length" class="subscribe-list-container">
          <div class="subscribe-list">
            <div
              v-for="(item, index) in subscribes"
              :key="index"
              class="subscribe-list-item rounded-8px mb-3"
              @click="handleSubscribeProducts(item)"
            >
              <SubscribeItemContent :item="item" :get-unit-price="getUnitPrice" :is-zero="isZero" />
            </div>
          </div>
        </div>

        <div v-if="templateProducts?.length" ref="purchaseSwiperRef" class="bottom-swiper purchase-swiper-bg swiper mt-1">
          <div class="swiper-wrapper">
            <div
              v-for="(item, index) in templateProducts"
              :key="index"
              class="swiper-slide bottom-swiper-item"
              @click="handlePayTemplate(item, index)"
            >
              <PurchaseItemContent :item="item" :is-active="active === index" />
            </div>
          </div>
          <div v-if="templateProducts.length > 1" class="bottom-swiper-pagination">
            <span
              v-for="(_, i) in templateProducts"
              :key="i"
              class="swiper-pagination-bullet"
              :class="{ 'swiper-pagination-bullet-active': i === purchaseSlideIndex }"
              @click="onPurchaseBulletClick(i)"
            />
          </div>
        </div>
      </div>

      <div v-if="!loaded" class="h-260px flex items-center justify-center flex-col">
        <BasicSpinner class="mx-auto" />
        <div class="mt-2 font-bold">
          {{ config.texts.loading }}
        </div>
      </div>
    </div>
  </BasicPopup>
</template>

<style lang="scss" scoped>
.popup-recharge-novelarc {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-height: 95dvh;
}

.payment-content-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

.subscribe-list-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
  position: relative;
}

.subscribe-list {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 10px;
  width: 100%;
  box-sizing: border-box;
  overscroll-behavior: contain;
  touch-action: pan-y;
}

.subscribe-list-item {
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.02);
  }

  &:last-child {
    margin-bottom: 0 !important;
  }
}

.purchase-list-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
  position: relative;
}

.purchase-list {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 10px;
  width: 100%;
  box-sizing: border-box;
  overscroll-behavior: contain;
  touch-action: pan-y;
}

.purchase-list-item {
  background: #FFF4EC;
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.02);
  }

  &:last-child {
    margin-bottom: 0 !important;
  }
}

.purchase-swiper-bg {
  background: #FFF4EC;
}

.bottom-swiper {
  flex-shrink: 0;
  width: 100%;
  overflow: hidden;
  position: relative;
  border-radius: 8px;
  height: 91px;

  .bottom-swiper-item {
    width: 100%;
    height: 91px;
    cursor: pointer;
  }

  .bottom-swiper-pagination {
    position: absolute;
    bottom: 10px;
    left: 0;
    right: 0;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &.subscribe-swiper-mode {
    height: 100px;

    .bottom-swiper-item {
      height: 100px;
    }

    .bottom-swiper-item :deep(.subscribe-item-novelarc) {
      width: 100%;
      height: 100%;
      min-height: unset;
      box-sizing: border-box;
    }
  }
}
</style>

<style lang="scss">
.popup-recharge-novelarc {
  .bottom-swiper-pagination {
    .swiper-pagination-bullet {
      display: inline-block;
      width: 6px;
      height: 6px;
      margin: 0 4px;
      background: #D9D9D9;
      border-radius: 3px;
      opacity: 1;
      cursor: pointer;
    }

    .swiper-pagination-bullet-active {
      width: 20px;
      height: 6px;
      background: linear-gradient(90deg, #F7A82A 0%, #FE7E01 100%);
      border-radius: 3px;
      opacity: 1;
    }
  }
}
</style>
